import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import hash from 'object-hash';
import { path, pluck, filter, prop, chain, uniqBy } from 'ramda';
import { FormattedMessage } from 'react-intl';

import { bookingShape, legShape } from 'constants/propTypes';
import { marginTariff } from 'constants/tariffCodes';
import { getPassengersWithExchangeInTicketChain } from 'utils/isReAccommodation';

import JourneySegment from 'components/shared/JourneySegment';

import { getColumns, mapToPassengersData, getWithExchangedProducts } from './JourneyLegs.utils';
import { StyledRow } from './JourneyLegs.styles';
import FareAddOnLeg from '../FareAddOnLeg';
import TrainMargin from '../TrainMargin';
import { isBookingMultiJourney } from 'utils/booking';
import { isSupportedExternalLeg } from '../AfterSalesCtas/CloneExternalTicketsCta/CloneExternalTicketsCta.utils';

const notCancelled = filter((i) => !i.cancelled);
const isActiveBookingItem = ({ cancelled, isActive, isPlaceholder }) => !cancelled && isActive && !isPlaceholder;

const getItemRefs = pluck('itemRef');

const { MARGIN_TRAIN, MARGIN_TRAIN_ER } = marginTariff;
const applicableTrainMarginTariffs = [MARGIN_TRAIN, MARGIN_TRAIN_ER];

const getCancelledLoyaltyItems = (legsWithExchangedProducts) => {
  const passengersWithExchange = getPassengersWithExchangeInTicketChain(legsWithExchangedProducts);

  return legsWithExchangedProducts.flatMap(({ products = [] }) =>
    products
      .filter((product) => passengersWithExchange.has(product.passengerId) && product.isActive && !product.isCancelled)
      .flatMap((product) => (product.associatedItemRefs?.length > 0 ? product.associatedItemRefs : [product.itemRef]))
  );
};

const JourneyLegs = ({
  bookingItems,
  legs,
  legsWithExchangedProducts,
  direction,
  includeFareConditions,
  isGroupChecked,
  isSelectable,
  toggleGroup,
  currency,
  reference,
  location: { pathname },
  booking,
  isOutboundSummatedFare,
  isInboundSummatedFare,
  canSelectExternalTicketsIndependently,
  intl,
  ticketDetails
}) => {
  const multiInvBookingItems = chain(prop('bookingItems'), legs);
  const cancelledLoyaltyItems = booking.isLoyalty ? getCancelledLoyaltyItems(legsWithExchangedProducts) : [];

  const isMultiJourney = isBookingMultiJourney(booking?.additionalDetails);

  const { outboundWithExchangedProducts, inboundWithExchangedProducts, status } = booking;

  return (
    <StyledRow>
      {legs.length > 0 && (
        <h3>
          <FormattedMessage id={`journeySummary.${direction}`} />
        </h3>
      )}

      {legs.map((leg) => {
        const selectItemIndependently = canSelectExternalTicketsIndependently && isSupportedExternalLeg(leg);
        const legBookingItems = leg.isMultiInventory ? multiInvBookingItems : prop('bookingItems', leg);
        const activeBookingItems = notCancelled(legBookingItems);
        const numberOfActivePassengers = uniqBy(prop('passengerId'), filter(isActiveBookingItem, legBookingItems)).length;
        const headerBookingItems = selectItemIndependently ? notCancelled(prop('bookingItems', leg)) : activeBookingItems;
        const itemRefs = getItemRefs(headerBookingItems);

        const formatHeader = (col) => {
          if (col.key === 'select') {
            return (
              <input type="checkbox" checked={isGroupChecked(itemRefs)} onChange={toggleGroup(itemRefs)} disabled={!itemRefs.length} />
            );
          }

          return <FormattedMessage id={`journeySegment.${col.key}`} />;
        };

        const withExchangedProducts = getWithExchangedProducts({ direction, outboundWithExchangedProducts, inboundWithExchangedProducts });

        const mainSegment = (
          <JourneySegment
            key={hash(leg)}
            status={status}
            bookingReference={reference}
            journey={leg}
            currency={currency}
            formatHeader={formatHeader}
            includeFareConditions={includeFareConditions}
            numberOfPassengers={uniqBy(prop('passengerId'), legBookingItems).length}
            numberOfActivePassengers={numberOfActivePassengers}
            columns={getColumns({
              booking,
              legBookingItems,
              addOnLeg: leg.addOnLeg,
              isGroupChecked,
              isSelectable,
              toggleGroup,
              targetUrl: `/manage/bookings/${reference}/amend/${leg.id}`,
              pathname,
              isMultiInventory: leg.isMultiInventory,
              isMultiJourney,
              isOutboundSummatedFare,
              isInboundSummatedFare,
              selectItemIndependently
            })}
            buildRow={(row, i) => mapToPassengersData(row, i, leg, booking, bookingItems, intl, ticketDetails)}
            cancelledLoyaltyItems={cancelledLoyaltyItems}
            withExchangedProducts={withExchangedProducts}
          />
        );

        const fareAddOnSegment = leg.addOnLeg && (
          <FareAddOnLeg
            direction={direction}
            includeFareConditions={includeFareConditions}
            activeBookingItems={(leg.addOnLeg.bookingItems || []).filter(({ cancelled }) => !cancelled)}
            currency={currency}
            formatHeader={formatHeader}
            key={leg.addOnLeg.id}
            leg={leg.addOnLeg}
            bookingItems={leg.addOnLeg.bookingItems}
            isGroupChecked={isGroupChecked}
            isSelectable={canSelectExternalTicketsIndependently && isSupportedExternalLeg(leg.addOnLeg)}
            toggleGroup={toggleGroup}
          />
        );

        const trainMarginProduct = leg.extraProducts.find((product) => applicableTrainMarginTariffs.includes(product.code));

        const trainMargin = trainMarginProduct && <TrainMargin product={trainMarginProduct} />;
        let orderOfLegs;

        const isAddOnBeforeMainLeg = path(['addOnLeg', 'origin', 'isPseudoStation']);

        if (isAddOnBeforeMainLeg(leg)) {
          orderOfLegs = [fareAddOnSegment, mainSegment, trainMargin];
        } else {
          orderOfLegs = [mainSegment, fareAddOnSegment, trainMargin];
        }

        return <Fragment key={hash(leg)}>{orderOfLegs}</Fragment>;
      })}
    </StyledRow>
  );
};

JourneyLegs.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string
  }),
  legs: PropTypes.arrayOf(legShape),
  legsWithExchangedProducts: PropTypes.arrayOf(legShape),
  direction: PropTypes.string.isRequired,
  includeFareConditions: PropTypes.bool,
  isGroupChecked: PropTypes.func.isRequired,
  isSelectable: PropTypes.bool,
  toggleGroup: PropTypes.func.isRequired,
  currency: PropTypes.string,
  reference: PropTypes.string,
  booking: bookingShape.isRequired,
  isOutboundSummatedFare: PropTypes.bool,
  isInboundSummatedFare: PropTypes.bool,
  canSelectExternalTicketsIndependently: PropTypes.bool,
  bookingItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      costPrice: PropTypes.shape({
        amount: PropTypes.number,
        currency: PropTypes.string
      })
    })
  ),
  intl: PropTypes.shape({ formatMessage: PropTypes.func }),
  ticketDetails: PropTypes.arrayOf(PropTypes.object)
};

export default JourneyLegs;
